import { useFormik } from "formik";
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { RESET_SPECIFIC_CATEGORIES } from "../../store/Types/CategoryTypes";
import {
  addSubCategory,
  getCategory,
  getSubCategory,
  getspecificSubCategory,
  updateSubCategory,
} from "../../store/AsyncMethods/CategoryMethod";
import * as Yup from "yup";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { RESET_ERROR } from "../../store/Types/AuthTypes";
import { toast } from "react-toastify";
import { Divider } from "primereact/divider";
import { Dropdown } from "primereact/dropdown";
import InputFocus from "../../hooks/InputFocus";

export default function EditSubCategories() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const inputRef = InputFocus();

  const { error, user } = useSelector((state) => state.AuthReducer);
  const { categories, specificSubCategory } = useSelector(
    (state) => state.CategoryReducer
  );

  const formik = useFormik({
    initialValues: {
      subCategory: "",
      categoryId: "",
      suggestions: [],
    },
    validationSchema: Yup.object({
      subCategory: Yup.string().required("Sub Category Required"),
      categoryId: Yup.number().required("Category Required"),
    }),
    onSubmit: async (data) => {
      dispatch(updateSubCategory(data, id)).then((success) => {
        if (success) {
          formik.resetForm();
          dispatch(getSubCategory());
          navigate("/sub-categories");
        }
      });
    },
  });

  // Suggestions state for input
  const [suggestionInput, setSuggestionInput] = React.useState("");

  // Populate suggestions from backend
  useEffect(() => {
    if (specificSubCategory) {
      if (specificSubCategory?.name) {
        formik.setFieldValue("subCategory", specificSubCategory.name);
      } else {
        formik.setFieldValue("category", "");
      }
      if (specificSubCategory?.category_id) {
        formik.setFieldValue("categoryId", specificSubCategory.category_id);
      } else {
        formik.setFieldValue("categoryId", "");
      }
      if (Array.isArray(specificSubCategory?.suggestions)) {
        formik.setFieldValue("suggestions", specificSubCategory.suggestions);
      } else {
        formik.setFieldValue("suggestions", []);
      }
    }
    // eslint-disable-next-line
  }, [specificSubCategory]);

  const handleAddSuggestion = (e) => {
    e.preventDefault();
    const value = suggestionInput.trim();
    if (value && !formik.values.suggestions.includes(value)) {
      formik.setFieldValue("suggestions", [...formik.values.suggestions, value]);
      setSuggestionInput("");
    }
  };

  const handleRemoveSuggestion = (keyword) => {
    formik.setFieldValue(
      "suggestions",
      formik.values.suggestions.filter((k) => k !== keyword)
    );
  };

  useEffect(() => {
    if (user) {
      dispatch(getCategory());
    }
  }, []);

  useEffect(() => {
    dispatch(getspecificSubCategory(id));
  }, []);

  return (
    <div className="mx-4">
      <div>
        <Button
          label="Back"
          icon="pi pi-arrow-left"
          className="p-black-btn"
          onClick={() => navigate(-1)}
        />
      </div>
      <form onSubmit={formik.handleSubmit}>
        <div className="card shadow-md rounded-lg p-4 mt-4">
          <Divider>
            <span className="text-2xl font-bold p-font-style text-center text-black mx-1">
              Edit Sub Category
            </span>
          </Divider>

          <div className="px-4 gap-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            <div className="">
              <div className="flex flex-col gap-2">
                <label htmlFor="categoryId" className="">
                  Select Category
                </label>
                <Dropdown
                  placeholder="Select"
                  id="categoryId"
                  name="categoryId"
                  className="!w-full text-lg p-primary-input"
                  value={formik.values.categoryId}
                  onChange={formik.handleChange}
                  options={categories}
                  optionLabel="name"
                  optionValue="id"
                  filter
                  pt={{
                    root: { className: "w-full" },
                    input: { className: "w-full p-primary-input" },
                    filterIcon: { className: "ml-1" },
                    filterInput: { className: "pl-6" },
                  }}
                />
              </div>
              {formik.touched?.categoryId && formik.errors?.categoryId && (
                <div className="p-error">{formik.errors?.categoryId}</div>
              )}
            </div>

            <div className="">
              <div className="flex flex-col gap-2">
                <label htmlFor="subCategory" className="">
                  Enter Sub Category
                </label>
                <span className=" w-full">
                  <InputText
                    ref={inputRef}
                    id="subCategory"
                    name="subCategory"
                    className="w-full text-lg p-primary-input"
                    value={formik.values.subCategory}
                    onChange={formik.handleChange}
                  />
                </span>
              </div>
              {formik.touched?.subCategory && formik.errors?.subCategory && (
                <div className="p-error">{formik.errors?.subCategory}</div>
              )}
            </div>

            {/* Suggestions input section */}
            <div className="col-span-1 md:col-span-2 lg:col-span-3 xl:col-span-4">
              <div className="flex flex-col gap-2 mt-4">
                <label htmlFor="suggestions" className="">
                  Suggestions (Keywords)
                </label>
                <form onSubmit={handleAddSuggestion} className="flex gap-2">
                  <InputText
                    id="suggestionInput"
                    name="suggestionInput"
                    className="w-full text-lg p-primary-input"
                    value={suggestionInput}
                    onChange={(e) => setSuggestionInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleAddSuggestion(e);
                    }}
                    placeholder="Type a keyword and press Enter or Add"
                  />
                  <Button
                    type="button"
                    label="Add"
                    icon="pi pi-plus"
                    className="p-primary-btn"
                    onClick={handleAddSuggestion}
                  />
                </form>
                <div className="flex flex-wrap gap-2 mt-2">
                  {formik.values.suggestions.map((keyword, idx) => (
                    <span key={idx} className="bg-gray-200 px-3 py-1 rounded-full flex items-center gap-1">
                      {keyword}
                      <span
                        style={{ cursor: 'pointer', fontWeight: 'bold', marginLeft: 4 }}
                        onClick={() => handleRemoveSuggestion(keyword)}
                        aria-label={`Remove ${keyword}`}
                        title={`Remove ${keyword}`}
                      >
                        ×
                      </span>
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className="mt-16">
            <div className="flex justify-end gap-4">
              <Button
                label="Clear"
                icon="pi pi-times"
                className="p-red-btn w-28"
                type="button"
                onClick={() => {
                  formik.resetForm();
                  dispatch({ type: RESET_SPECIFIC_CATEGORIES });
                }}
              />
              <Button
                label={"Edit"}
                icon="pi pi-check"
                className="p-secondary-btn w-28"
                type="submit"
              />
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
